import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowRightCircle, Users, LayoutGrid, ShieldCheck, Banknote, HeartPulse, ArrowRight, Sparkles } from 'lucide-react';
import { PRODUCT_SUITES } from '../lib/caraloom_v2_ia';
import { Seo } from '../components/seo/Seo';
import { Footer } from '../components/brand/Footer';
import { PublicNavbar } from '../components/brand/PublicNavbar';
import { DischargeSandboxTile } from '../components/brand/DischargeSandboxTile';

const ICON_MAP = {
  'arrow-right-circle': ArrowRightCircle,
  'users':              Users,
  'layout-grid':        LayoutGrid,
  'shield-check':       ShieldCheck,
  'banknote':           Banknote,
  'heart-pulse':        HeartPulse,
};

// Each suite gets its own "what's inside" bullets + buyer CTA.
const SUITE_DETAIL = {
  referrals: {
    pitch: [
      'Hospital case managers send a discharge in <2 minutes via the public Discharge API or by pasting a FHIR R4 Bundle — CaraLoom pre-fills the entire referral.',
      'AI orchestrator scores intake, eligibility, and geo-fit. Average time-to-first-accept is under 60 minutes across the network.',
      'Bidirectional FHIR mirroring keeps EMR and CaraLoom in sync so no one re-keys patient data.',
    ],
    headline_metric: '<60 min',
    headline_metric_label: 'Median time from referral submitted → provider accepted',
    cta_primary: { label: 'Send a Referral', to: '/for-discharge-planners' },
    cta_secondary: { label: 'Try the FHIR Ingest', to: '/auth?next=/referrals/ingest-fhir' },
  },
  workforce: {
    pitch: [
      'The largest verified marketplace of independent post-acute clinicians — RN, LPN, PT, OT, SLP, MSW, Aide.',
      'AI matching looks at modality, specialty, geo, history, and rating to fill a visit within 24 hours of the request 85%+ of the time.',
      'Credentialing is continuous (license + CAQH + payer rosters). Providers never staff an unverified clinician.',
    ],
    headline_metric: '85%',
    headline_metric_label: 'Visit fill rate within 24 hours of need',
    cta_primary: { label: 'Find a Clinician', to: '/marketplace' },
    cta_secondary: { label: 'For Care Providers', to: '/auth' },
  },
  operations: {
    pitch: [
      'A single executive screen for the COO/CFO: live census, visits-per-clinician-per-day, A/R aging, and probability-weighted 30/60/90-day revenue forecast.',
      'Built for the daily ritual — the one screen that gets screenshotted for the board pack.',
      'Drill-downs map to the underlying claim, engagement, and visit so nothing is hidden behind an opaque KPI.',
    ],
    headline_metric: '+18%',
    headline_metric_label: 'Lift in visits per clinician per day (vs. baseline)',
    cta_primary: { label: 'See the Executive Dashboard', to: '/auth?next=/operations/executive' },
    cta_secondary: { label: 'Talk to Operations Team', to: '/contact' },
  },
  compliance: {
    pitch: [
      'Survey-ready, always. CaraLoom continuously checks license expirations, OIG/SAM exclusions, and CMS Conditions of Participation against every active clinician.',
      'PHI minimization, audit trails, BAA-backed subprocessors. Designed for hospice/home-health/SNF compliance officers.',
      'When something drifts — an expired CPR cert, a missed annual TB screen — the Compliance suite raises it before survey day, not on it.',
    ],
    headline_metric: '0 days',
    headline_metric_label: 'Days to survey readiness — always ready, by design',
    cta_primary: { label: 'Compliance Datasheet', to: '/trust-safety' },
    cta_secondary: { label: 'Request a BAA', to: '/contact' },
  },
  'revenue-cycle': {
    pitch: [
      'Pre-submission AI denial prediction with concrete, payer-specific suggested edits — reduces denials 18–35% in industry comp sets.',
      'EDI 837P submission, ERA posting, secondary-claim automation, and live A/R aging in one workspace.',
      'Self-pay → Stripe payment links and statement automation. One pipeline for insurance + private-pay.',
    ],
    headline_metric: '<35 days',
    headline_metric_label: 'Days Sales Outstanding (target across the network)',
    cta_primary: { label: 'See Denial Prediction', to: '/auth?next=/clinician/denial-prediction' },
    cta_secondary: { label: 'Talk to RCM Team', to: '/contact' },
  },
  care: {
    pitch: [
      'The digital care hub for patients and families: medication tracking, care-plan visibility, family-shared updates, and concierge scheduling.',
      'Connects post-discharge care episodes to the home and keeps the family loop active long after the SNF or hospital stay.',
      'Private-pay add-ons (companionship, transport, home modifications) extend the revenue runway per episode.',
    ],
    headline_metric: 'Monthly',
    headline_metric_label: 'Active families retained after primary episode ends',
    cta_primary: { label: 'For Families', to: '/marketplace' },
    cta_secondary: { label: 'How CaraLoom Works', to: '/how-it-works' },
  },
};

export default function SuitePage() {
  const { slug } = useParams();
  const suite = PRODUCT_SUITES.find((s) => s.id === slug);

  if (!suite) {
    return <Navigate to="/" replace />;
  }

  const Icon = ICON_MAP[suite.icon] || Sparkles;
  const detail = SUITE_DETAIL[suite.id] || {};
  const otherSuites = PRODUCT_SUITES.filter((s) => s.id !== suite.id);

  const path = `/suites/${suite.id}`;
  const title = `${suite.name} — Post-Acute Care Operating System | CaraLoom`;

  return (
    <div className="min-h-screen bg-cn-bone-50 dark:bg-cn-ink-900" data-testid={`suite-page-${suite.id}`}>
      <Seo
        title={title}
        description={`${suite.tagline} Built for ${suite.primary_buyer}.`}
        path={path}
        keywords={[suite.name, suite.primary_buyer, 'post-acute care', 'CaraLoom']}
      />

      <PublicNavbar />

      {/* HERO */}
      <section
        className="cn-section pt-24 pb-16 lg:pt-32 lg:pb-24 border-b border-cn-border"
        style={{ background: `linear-gradient(135deg, ${suite.color}0d 0%, transparent 60%)` }}
      >
        <div className="cn-container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 cn-trust-badge mb-5" data-testid="suite-eyebrow">
                <Icon className="w-3.5 h-3.5" style={{ color: suite.color }} />
                One of six CaraLoom Suites
              </div>
              <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-cn-text dark:text-white leading-[1.05] tracking-tight"
                  data-testid="suite-headline">
                {suite.name}
              </h1>
              <p className="mt-5 text-lg text-cn-text-muted leading-relaxed max-w-xl" data-testid="suite-tagline">
                {suite.tagline}
              </p>
              <p className="mt-3 text-sm text-cn-text-muted/80 italic">
                Built for {suite.primary_buyer}.
              </p>

              <div className="mt-8 flex flex-wrap gap-3" data-testid="suite-cta-row">
                {detail.cta_primary && (
                  <Link
                    to={detail.cta_primary.to}
                    className="cn-btn cn-btn-primary h-12 px-6 inline-flex items-center gap-2"
                    data-testid="suite-cta-primary"
                  >
                    {detail.cta_primary.label}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
                {detail.cta_secondary && (
                  <Link
                    to={detail.cta_secondary.to}
                    className="cn-btn cn-btn-ghost h-12 px-6 inline-flex items-center"
                    data-testid="suite-cta-secondary"
                  >
                    {detail.cta_secondary.label}
                  </Link>
                )}
              </div>
            </div>

            {/* Right: headline metric card */}
            <div
              className="cn-card p-8 lg:p-10 border-2"
              style={{ borderColor: `${suite.color}33` }}
              data-testid="suite-metric-card"
            >
              <div className="cn-eyebrow mb-4" style={{ color: suite.color }}>
                North-star KPI
              </div>
              <div className="font-display font-bold text-6xl tracking-tight"
                   style={{ color: suite.color }}
                   data-testid="suite-headline-metric">
                {detail.headline_metric || '—'}
              </div>
              <p className="mt-3 text-sm text-cn-text-muted leading-relaxed">
                {detail.headline_metric_label || suite.kpi}
              </p>

              <div className="mt-6 pt-6 border-t border-cn-border">
                <div className="cn-eyebrow mb-2">Monetization</div>
                <p className="text-sm text-cn-text">{suite.monetization}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT'S INSIDE */}
      <section className="cn-section py-16 lg:py-24" data-testid="suite-pitch-section">
        <div className="cn-container max-w-5xl">
          <div className="cn-eyebrow mb-4">What&apos;s inside</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-cn-text dark:text-white mb-10">
            How {suite.name.replace('CaraLoom ', '')} works in practice
          </h2>
          <ul className="space-y-6">
            {(detail.pitch || []).map((line, i) => (
              <li
                key={i}
                className="flex gap-4 cn-card p-6"
                data-testid={`suite-pitch-${i}`}
              >
                <span
                  className="w-10 h-10 shrink-0 rounded-xl flex items-center justify-center font-display font-bold text-white"
                  style={{ background: suite.color }}
                >
                  {i + 1}
                </span>
                <p className="text-base text-cn-text dark:text-white leading-relaxed">{line}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* DISCHARGE API SANDBOX (Referrals suite only) */}
      {suite.id === 'referrals' && <DischargeSandboxTile accentColor={suite.color} />}

      {/* OTHER SUITES */}
      <section className="cn-section py-16 lg:py-24 border-t border-cn-border bg-cn-bone-100/40 dark:bg-cn-ink-800/30"
               data-testid="suite-cross-sell">
        <div className="cn-container">
          <div className="cn-eyebrow mb-3">The full operating system</div>
          <h2 className="font-display font-bold text-2xl sm:text-3xl text-cn-text dark:text-white mb-8">
            Five more suites in the CaraLoom platform
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="suite-cross-grid">
            {otherSuites.map((s) => {
              const I = ICON_MAP[s.icon] || Sparkles;
              return (
                <Link
                  key={s.id}
                  to={`/suites/${s.id}`}
                  className="cn-card p-6 group hover:-translate-y-0.5 transition-transform"
                  data-testid={`suite-cross-${s.id}`}
                >
                  <I className="w-5 h-5 mb-3" style={{ color: s.color }} />
                  <h3 className="font-display font-semibold text-lg text-cn-text dark:text-white group-hover:text-cn-primary transition-colors">
                    {s.name.replace('CaraLoom ', '')}
                  </h3>
                  <p className="mt-1 text-sm text-cn-text-muted leading-snug line-clamp-2">{s.tagline}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-cn-primary opacity-0 group-hover:opacity-100 transition-opacity">
                    Learn more <ArrowRight className="w-3 h-3" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
